import { UserNotificationsNotifications, UserNotificationsNotificationsEntityTypeEnum } from "@microtica/ms-notification-sdk";
import { Tag, Typography, notification } from "antd";
import {
    Bell,
    AlertTriangle,
    Rocket,
    GitBranch,
    Server,
    ArrowRight,
    Calendar,
    CalendarClock,
    CheckCircle,
    XCircle,
    Clock,
    Ban,
    CircleStop,
    Play
} from "lucide-react";
import moment from "moment-timezone";
import { useNavigate } from "react-router";
import { getNotificationService } from "../../backend";
import AlarmSeverityTag, { Severity } from "../../components/alarms/AlarmSeverityTag";

interface NotificationProps extends UserNotificationsNotifications {
    projectName: string;
    type: "Incidents" | "Updates";
    onClose: () => void;
    updateProjectInfo: (projectId: string) => Promise<void>;
}

const Notification = (data: NotificationProps) => {
    const navigate = useNavigate();
    const { Text } = Typography;

    const handleOnClick = async () => {
        await Promise.all([
            getNotificationService().markNotificationAsRead(data.id, data.projectId),
            data.updateProjectInfo(data.projectId)
        ]);

        data.onClose();

        if (data.type === "Incidents") {
            if (data.status === "ALARM") {
                navigate(`/projects/${data.projectId}/monitoring/alarms?id=${data.itemId}`);
            } else {
                notification.info({
                    message: "Resolved alarms are not shown in the Alarms page"
                })
                navigate(`/projects/${data.projectId}/monitoring/alarms`);
            }
        } else if (data.entityType === UserNotificationsNotificationsEntityTypeEnum.MICROSERVICE) {
            if (data.status === "UNDEPLOYED" || data.status === "FAILED") {
                const clusterComponent = data.additionalInfo?.clusterId.replace(`${data.additionalInfo?.stageId}-`, "");
                navigate(`/projects/${data.projectId}/environments/${data.additionalInfo?.stageId}/components/${clusterComponent}`);
            } else {
                navigate(`/projects/${data.projectId}/environments/${data.additionalInfo?.stageId}/components/${data.additionalInfo?.clusterId}/apps/${data.additionalInfo?.namespace}/${data.itemId}/overview`);
            }
        } else if (data.entityType === UserNotificationsNotificationsEntityTypeEnum.STAGE) {
            navigate(`/projects/${data.projectId}/environments/${data.itemId}`);
        } else if (data.entityType as any === UserNotificationsNotificationsEntityTypeEnum.PIPELINE) {
            navigate(`/projects/${data.projectId}/pipelines?pipelineId=${data.itemId}&buildId=${data.version}`);
        } else if (data.entityType as any === "SCHEDULE") {
            navigate(`/projects/${data.projectId}/cost-explorer?view=optimizer`);
        } else {
            navigate(`/projects/${data.projectId}`);
        }
        return;
    }

    // Get entity type icon
    const getEntityIcon = () => {
        switch (data.entityType) {
            case "MICROSERVICE":
                return <Rocket size={16} />;
            case "PIPELINE":
                return <GitBranch size={16} />;
            case "STAGE":
                return <Server size={16} />;
            default:
                if ((data.entityType as any) === "SCHEDULE") {
                    return <CalendarClock size={16} />;
                }
                return data.type === "Incidents" ? <AlertTriangle size={16} /> : <Bell size={16} />;
        }
    };

    // Get entity type label
    const getEntityLabel = () => {
        switch (data.entityType) {
            case "MICROSERVICE":
                return "Application";
            case "PIPELINE":
                return "Pipeline";
            case "STAGE":
                return "Component";
            default:
                if ((data.entityType as any) === "SCHEDULE") {
                    return "Saving Schedule";
                }
                return data.entityType;
        }
    };

    // Determine icon status class
    const getIconStatusClass = () => {
        const status = data.status.toLowerCase();
        if (status.includes("complete") || status.includes("deployed") || status === "ok" || status === "succeeded" || status === "successfully") {
            return "success";
        }
        if (status.includes("failed") || status.includes("error") || status === "alarm" || status === "aborted" || status === "timed_out") {
            return "error";
        }
        // In-progress states shown as "started" - use default styling
        if (status.includes("progress") || status.includes("running") || status === "building" || status === "deploying") {
            return "default";
        }
        return "default";
    };

    // Get status display info (icon, color, label)
    const getStatusInfo = () => {
        const status = data.status.toLowerCase();

        // Success statuses
        if (status === "succeeded" || status === "completed" || status === "deployed" || status === "successfully") {
            return {
                icon: <CheckCircle size={10} />,
                color: "#059669",
                bgColor: "rgba(5, 150, 105, 0.1)",
                label: status === "successfully" ? "Success" : "Succeeded"
            };
        }

        // In progress statuses - show as "Started" since notifications are point-in-time
        if (status === "building" || status === "in_progress" || status === "deploying" || status === "running") {
            return {
                icon: <Play size={10} />,
                color: "#3b82f6",
                bgColor: "rgba(59, 130, 246, 0.1)",
                label: "Started"
            };
        }

        // Failed statuses
        if (status === "failed" || status === "error") {
            return {
                icon: <XCircle size={10} />,
                color: "#ef4444",
                bgColor: "rgba(239, 68, 68, 0.1)",
                label: "Failed"
            };
        }

        // Aborted status
        if (status === "aborted" || status === "cancelled") {
            return {
                icon: <Ban size={10} />,
                color: "#f59e0b",
                bgColor: "rgba(245, 158, 11, 0.1)",
                label: "Aborted"
            };
        }

        // Timed out status
        if (status === "timed_out") {
            return {
                icon: <Clock size={10} />,
                color: "#f59e0b",
                bgColor: "rgba(245, 158, 11, 0.1)",
                label: "Timed Out"
            };
        }

        // Stopped status
        if (status === "stopped" || status === "undeployed") {
            return {
                icon: <CircleStop size={10} />,
                color: "#9ca3af",
                bgColor: "rgba(156, 163, 175, 0.1)",
                label: status === "undeployed" ? "Undeployed" : "Stopped"
            };
        }

        // Queued/Pending status
        if (status === "queued" || status === "pending") {
            return {
                icon: <Clock size={10} />,
                color: "#9ca3af",
                bgColor: "rgba(156, 163, 175, 0.1)",
                label: "Queued"
            };
        }

        // Default
        return {
            icon: null,
            color: "#9ca3af",
            bgColor: "rgba(156, 163, 175, 0.1)",
            label: status.replace(/_/g, ' ')
        };
    };

    // Render status tag
    const renderStatusTag = () => {
        // No status tag for saving schedule
        if ((data.entityType as any) === "SCHEDULE") {
            return null;
        }

        const statusInfo = getStatusInfo();

        return (
            <span
                className="notification-status-tag"
                style={{
                    color: statusInfo.color,
                    backgroundColor: statusInfo.bgColor,
                    border: `1px solid ${statusInfo.color}20`
                }}
            >
                {statusInfo.icon}
                {statusInfo.label}
            </span>
        );
    };

    // Regular notification content
    const notificationContent = (
        <div className="notification-card-content">
            <div className="notification-card-main">
                <div className={`notification-card-icon ${getIconStatusClass()}`}>
                    {getEntityIcon()}
                </div>
                <div className="notification-card-body">
                    <div className="notification-card-header">
                        <span className="notification-card-entity">{getEntityLabel()}</span>
                        {renderStatusTag()}
                    </div>
                    <div className="notification-card-message">{data.message}</div>
                    <div className="notification-card-meta">
                        <span className="notification-card-project">{data.projectName}</span>
                        <span className="notification-card-time">
                            <Calendar size={12} />
                            {moment(parseInt(data.timestamp)).fromNow()}
                        </span>
                        {!data.isRead && <span className="notification-card-new-badge">NEW</span>}
                    </div>
                </div>
            </div>
            <div className="notification-card-arrow">
                <ArrowRight size={16} />
            </div>
        </div>
    );

    // Incident/Alarm notification content
    const alarmNotificationContent = (
        <div className="notification-card-content">
            <div className="notification-card-main">
                <div className={`notification-card-icon ${data.status === "OK" ? "success" : "error"}`}>
                    <AlertTriangle size={16} />
                </div>
                <div className="notification-card-body">
                    <div className="notification-card-header">
                        {data.status === "OK" ? (
                            <Tag color="green" className="notification-resolved-tag">Resolved</Tag>
                        ) : (
                            <AlarmSeverityTag severity={data.additionalInfo?.severity as Severity} />
                        )}
                    </div>
                    <Text
                        ellipsis={{ tooltip: data.additionalInfo?.alarmName }}
                        className="notification-card-alarm-name"
                    >
                        {data.additionalInfo?.alarmName}
                    </Text>
                    <div className="notification-card-details">
                        <span><strong>Env:</strong> {data.additionalInfo?.envId}</span>
                        <span><strong>App:</strong> {data.additionalInfo?.appName}</span>
                    </div>
                    <div className="notification-card-meta">
                        <span className="notification-card-project">{data.projectName}</span>
                        <span className="notification-card-time">
                            <Calendar size={12} />
                            {moment(parseInt(data.timestamp)).fromNow()}
                        </span>
                        {!data.isRead && <span className="notification-card-new-badge">NEW</span>}
                    </div>
                </div>
            </div>
            <div className="notification-card-arrow">
                <ArrowRight size={16} />
            </div>
        </div>
    );

    return (
        <div
            className={`notification-card ${data.isRead ? 'read' : 'unread'}`}
            onClick={handleOnClick}
        >
            {data.type === "Incidents" ? alarmNotificationContent : notificationContent}
        </div>
    )
}

export default Notification;
